import React, { useEffect, useState } from 'react';
import useScrollReveal from '../hooks/useScrollReveal';

const PrivacyPage = () => {
  const [mounted, setMounted] = useState(false);

  useScrollReveal({ threshold: 0.1, rootMargin: '-50px', oneWay: true });

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    window.scrollTo(0, 0);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ background: 'var(--bg-primary)', paddingTop: '80px' }}>
      {/* Hero */}
      <section style={{ padding: 'clamp(6rem, 12vw, 8rem) 0' }}>
        <div className="container-narrow" style={{ textAlign: 'center' }}>
          <h1
            style={{
              fontSize: 'clamp(3rem, 6vw, 5rem)',
              lineHeight: '1.1',
              marginBottom: 'var(--space-lg)',
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 1s var(--ease-out) 0.2s',
            }}
          >
            Privacy Policy
          </h1>
          <p
            style={{
              fontSize: '1.1rem',
              color: 'var(--text-secondary)',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 1s var(--ease-out) 0.4s',
            }}
          >
            Effective Date: May 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: 'clamp(4rem, 8vw, 6rem) 0', background: 'var(--bg-secondary)' }}>
        <div className="container" style={{ maxWidth: '800px' }}>

          {/* Intro */}
          <div data-scroll>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-lg)' }}>
              Allyverto IT Technologies Private Limited ("Allyverto", "we", "our", or "us") values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your information when you use our applications, websites, and AI-powered services.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              By using our services, you agree to the practices described in this Privacy Policy.
            </p>
          </div>

          {/* 1. Information We Collect */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              1. Information We Collect
            </h2>

            <h3 style={{ fontSize: '1.3rem', marginBottom: 'var(--space-md)', fontWeight: 500 }}>Account Information</h3>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              When you create an account or sign in, we may collect:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Name</li>
              <li>Email address</li>
              <li>Profile picture (optional)</li>
              <li>Authentication information through Google Sign-In or traditional login systems</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-lg)' }}>
              Passwords used in traditional login systems are encrypted and securely stored.
            </p>

            <h3 style={{ fontSize: '1.3rem', marginBottom: 'var(--space-md)', fontWeight: 500 }}>Chat and Uploaded Content</h3>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Our services allow users to interact with AI systems through:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Text conversations</li>
              <li>Uploaded images</li>
              <li>Uploaded documents</li>
              <li>Voice input through microphone access</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              When you use these features, we may collect and process:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Chat prompts and responses</li>
              <li>Uploaded files and images</li>
              <li>Voice-to-text input</li>
              <li>Related interaction history</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-lg)' }}>
              This information is used solely to provide AI-powered functionality and improve user experience.
            </p>

            <h3 style={{ fontSize: '1.3rem', marginBottom: 'var(--space-md)', fontWeight: 500 }}>Location Information</h3>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-lg)' }}>
              Our application may request access to your device location to support location-based features. Location sharing is completely optional and controlled by the user. Users may deny or revoke location permissions at any time through device settings.
            </p>

            <h3 style={{ fontSize: '1.3rem', marginBottom: 'var(--space-md)', fontWeight: 500 }}>Cookies and Local Storage</h3>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We may use cookies, local storage, and similar technologies to:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)', paddingLeft: '1.5rem' }}>
              <li>Maintain login sessions</li>
              <li>Store user preferences</li>
              <li>Improve platform functionality</li>
              <li>Enhance performance and user experience</li>
            </ul>
          </div>

          {/* 2. How We Use Information */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              2. How We Use Information
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We use collected information to:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)', paddingLeft: '1.5rem' }}>
              <li>Provide and operate our services</li>
              <li>Authenticate users securely</li>
              <li>Deliver AI-generated responses</li>
              <li>Process uploaded images and documents</li>
              <li>Maintain chat history and user conversations</li>
              <li>Improve application functionality and stability</li>
              <li>Respond to support requests</li>
              <li>Protect platform security and prevent misuse</li>
            </ul>
          </div>

          {/* 3. AI Processing and Third-Party Services */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              3. AI Processing and Third-Party Services
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Our platform may use trusted cloud infrastructure, database systems, and AI service providers to securely process and deliver application functionality.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              User content may be processed through these services solely for providing requested features and improving user experience.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              We do not sell user data to third parties.
            </p>
          </div>

          {/* 4. Data Storage and Security */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              4. Data Storage and Security
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We implement reasonable administrative, technical, and security measures to protect user information from unauthorized access, misuse, alteration, or disclosure.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Sensitive information such as passwords is encrypted before storage.
            </p>
            <div style={{ padding: 'var(--space-lg)', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', marginBottom: 'var(--space-2xl)' }}>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.6', color: 'var(--text-muted)', margin: 0 }}>
                While we strive to protect your information, no method of electronic storage or internet transmission can be guaranteed to be completely secure.
              </p>
            </div>
          </div>

          {/* 5. Chat History and User Control */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              5. Chat History and User Control
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Chat history may be stored to improve user experience and maintain conversation continuity.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Users may:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Delete conversations within the application</li>
              <li>Request deletion of account data by contacting support</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              We will process verified deletion requests within a reasonable timeframe.
            </p>
          </div>

          {/* 6. Camera and Microphone Permissions */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              6. Camera and Microphone Permissions
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Our application may request:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Camera access for capturing and uploading images</li>
              <li>Microphone access for voice-to-text interactions</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              These permissions are used only when initiated by the user.
            </p>
          </div>

          {/* 7. Data Sharing */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              7. Data Sharing
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We do not sell, rent, or trade personal information.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We may share limited information with trusted infrastructure and service providers strictly for:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Hosting</li>
              <li>Authentication</li>
              <li>AI processing</li>
              <li>File storage</li>
              <li>Service operation</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Such providers are expected to maintain appropriate security protections.
            </p>
          </div>

          {/* 8. Children's Privacy */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              8. Children's Privacy
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Our services are intended for users aged 13 and above.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              We do not knowingly collect personal information from children under the age of 13. If we become aware that such information has been collected, we will take reasonable steps to delete it.
            </p>
          </div>

          {/* 9. Future Payments and Subscriptions */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              9. Future Payments and Subscriptions
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Our services currently do not include paid subscriptions or payment processing.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              If paid features or subscriptions are introduced in the future, this Privacy Policy may be updated to explain how payment-related information is handled.
            </p>
          </div>

          {/* 10. Your Rights */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              10. Your Rights
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Depending on your jurisdiction, you may have rights to:
            </p>
            <ul style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)', paddingLeft: '1.5rem' }}>
              <li>Access your information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your data</li>
              <li>Withdraw permissions</li>
              <li>Request information regarding stored data</li>
            </ul>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              To exercise these rights, contact us using the information below.
            </p>
          </div>

          {/* 11. Changes to This Privacy Policy */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              11. Changes to This Privacy Policy
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              We may update this Privacy Policy from time to time.
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-2xl)' }}>
              Updated versions will be posted on this page with a revised effective date. Continued use of our services after updates constitutes acceptance of the revised policy.
            </p>
          </div>

          {/* 12. Contact Us */}
          <div data-scroll>
            <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-lg)', fontFamily: 'var(--font-serif)' }}>
              12. Contact Us
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              If you have questions, requests, or concerns regarding this Privacy Policy or your data, contact:
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              <strong>Allyverto IT Technologies Private Limited</strong>
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)', marginBottom: 'var(--space-md)' }}>
              Email:{' '}
              <a href="mailto:admin@allyverto.com" style={{ color: 'var(--accent)', borderBottom: '1px solid var(--accent)' }}>
                admin@allyverto.com
              </a>
            </p>
            <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: 'var(--text-secondary)' }}>
              Website:{' '}
              <a href="https://allyverto.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', borderBottom: '1px solid var(--accent)' }}>
                https://allyverto.com
              </a>
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;